"use client";

import { Loader2 } from "lucide-react";
import type {
  PostRollDamageFeedbackEnvelope,
  PostRollFeedbackDecision,
  PostRollFeedbackEnvelope,
} from "@/lib/core/schemas/post-roll-feedback.schema";
import { cn } from "@/lib/utils";

type PostRollFeedbackOfferProps = {
  envelope: PostRollFeedbackEnvelope;
  disabled?: boolean;
  onDecision: (decision: PostRollFeedbackDecision) => void;
};

export function PostRollFeedbackOffer({
  envelope,
  disabled = false,
  onDecision,
}: PostRollFeedbackOfferProps) {
  return (
    <div className="my-2 rounded-lg border border-amber-500/50 bg-card p-4">
      <div className="mb-1 font-mono text-[10px] text-muted-foreground uppercase tracking-widest">
        Roll Adjustment
      </div>
      <p className="mb-3 text-card-foreground text-sm">
        Original total:{" "}
        <span className="font-semibold tabular-nums">
          {envelope.snapshot.originalTotal}
        </span>
      </p>
      <div className="flex flex-wrap gap-2">
        {envelope.options.map((option) => (
          <button
            className={cn(
              "rounded border border-border px-3 py-1 font-medium text-xs hover:bg-accent",
              "disabled:pointer-events-none disabled:opacity-50"
            )}
            disabled={disabled}
            key={option.id}
            onClick={() => onDecision({ kind: "apply", optionId: option.id })}
            type="button"
          >
            {disabled ? (
              <span className="flex items-center gap-1.5">
                <Loader2 className="h-3 w-3 animate-spin" />
                {option.label}
              </span>
            ) : (
              option.label
            )}
          </button>
        ))}
        <button
          className={cn(
            "rounded border border-border px-3 py-1 font-medium text-xs hover:bg-accent",
            "disabled:pointer-events-none disabled:opacity-50"
          )}
          disabled={disabled}
          onClick={() => onDecision({ kind: "decline" })}
          type="button"
        >
          Accept Result
        </button>
      </div>
    </div>
  );
}

type PostRollDamageFeedbackOfferProps = {
  envelope: PostRollDamageFeedbackEnvelope;
  disabled?: boolean;
  onDecision: (decision: PostRollFeedbackDecision) => void;
};

export function PostRollDamageFeedbackOffer({
  envelope,
  disabled = false,
  onDecision,
}: PostRollDamageFeedbackOfferProps) {
  return (
    <div className="my-2 rounded-lg border border-amber-500/50 bg-card p-4">
      <div className="mb-1 font-mono text-[10px] text-muted-foreground uppercase tracking-widest">
        Damage Adjustment
      </div>
      <p className="mb-3 text-card-foreground text-sm">
        Original damage:{" "}
        <span className="font-semibold tabular-nums">
          {envelope.snapshot.originalTotal}
        </span>
      </p>
      <div className="flex flex-wrap gap-2">
        {envelope.options.map((option) => (
          <button
            className={cn(
              "rounded border border-border px-3 py-1 font-medium text-xs hover:bg-accent",
              "disabled:pointer-events-none disabled:opacity-50"
            )}
            disabled={disabled}
            key={option.id}
            onClick={() => onDecision({ kind: "apply", optionId: option.id })}
            type="button"
          >
            {disabled ? (
              <span className="flex items-center gap-1.5">
                <Loader2 className="h-3 w-3 animate-spin" />
                {option.label}
              </span>
            ) : (
              option.label
            )}
          </button>
        ))}
        <button
          className={cn(
            "rounded border border-border px-3 py-1 font-medium text-xs hover:bg-accent",
            "disabled:pointer-events-none disabled:opacity-50"
          )}
          disabled={disabled}
          onClick={() => onDecision({ kind: "decline" })}
          type="button"
        >
          Accept Damage
        </button>
      </div>
    </div>
  );
}
